import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Users, Flame, DollarSign, Target, Plus, Shield, Trash2, X, Crown, Infinity as InfinityIcon, CreditCard, Check, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
    <div className="border border-zinc-900 bg-[#0c0c0c] p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
            <Icon className={`w-4 h-4 ${accent || "text-signal"}`} strokeWidth={1.5} />
        </div>
        <div className="font-heading font-black text-3xl">{value}</div>
    </div>
);

export default function Admin() {
    const { user, authHeaders } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [planFilter, setPlanFilter] = useState("all");

    // Add member modal
    const [modalOpen, setModalOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [addToMyTeam, setAddToMyTeam] = useState(true);
    const [makeAdmin, setMakeAdmin] = useState(false);
    const [makeUnlimited, setMakeUnlimited] = useState(true);  // default ON: admins rarely want to charge their own users
    const [creating, setCreating] = useState(false);

    // Platform settings (Stripe)
    const [settings, setSettings] = useState(null);
    const [stripeKey, setStripeKey] = useState("");
    const [savingStripe, setSavingStripe] = useState(false);
    const [testingStripe, setTestingStripe] = useState(false);
    const [testResult, setTestResult] = useState(null); // {ok, account, error}

    const loadSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/platform-settings`, { headers: authHeaders() });
            setSettings(res.data);
        } catch { /* noop */ }
    }, [authHeaders]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, u] = await Promise.all([
                axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
                axios.get(`${API}/admin/users`, {
                    headers: authHeaders(),
                    params: { q: q || undefined, plan: planFilter === "all" ? undefined : planFilter },
                }),
            ]);
            setStats(s.data);
            setUsers(u.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load admin data");
        } finally { setLoading(false); }
    }, [authHeaders, q, planFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSettings(); }, [loadSettings]);

    const testStripeKey = async () => {
        const k = (stripeKey || "").trim();
        if (!k) {
            toast.error("Paste your Stripe Secret Key first.");
            return;
        }
        setTestingStripe(true);
        setTestResult(null);
        try {
            const res = await axios.post(
                `${API}/admin/platform-settings/stripe/test`,
                { stripe_api_key: k },
                { headers: authHeaders() }
            );
            setTestResult({ ok: true, account: res.data.account });
            toast.success(`Connected to ${res.data.account.email || res.data.account.id}`);
        } catch (err) {
            setTestResult({ ok: false, error: err?.response?.data?.detail || "Validation failed" });
        } finally { setTestingStripe(false); }
    };

    const saveStripeKey = async (clear = false) => {
        setSavingStripe(true);
        try {
            const res = await axios.post(
                `${API}/admin/platform-settings/stripe`,
                { stripe_api_key: clear ? "" : stripeKey.trim() },
                { headers: authHeaders() }
            );
            if (clear) {
                toast.success("Stripe key cleared — using platform default");
            } else {
                const acc = res.data?.account;
                toast.success(`Stripe connected — ${acc?.email || acc?.id || "ready to take payments"}`);
            }
            setStripeKey("");
            setTestResult(null);
            await loadSettings();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save Stripe key");
        } finally { setSavingStripe(false); }
    };

    const toggleAdmin = async (row) => {
        try {
            await axios.post(`${API}/admin/users/${row.id}/admin`, { value: !row.is_admin }, { headers: authHeaders() });
            toast.success(row.is_admin ? "Admin revoked" : "Admin granted (+ unlimited)");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const toggleUnlimited = async (row) => {
        try {
            await axios.post(
                `${API}/admin/teams/${row.team_id}/unlimited`,
                { value: !row.admin_unlimited },
                { headers: authHeaders() }
            );
            toast.success(row.admin_unlimited ? "Unlimited removed" : "Unlimited granted to team");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const deleteUser = async (row) => {
        if (!window.confirm(`Delete ${row.email}? This removes their account and all conversations.`)) return;
        try {
            await axios.delete(`${API}/admin/users/${row.id}`, { headers: authHeaders() });
            toast.success("User deleted");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const createMember = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setCreating(true);
        try {
            await axios.post(
                `${API}/admin/users`,
                { name: newName, email: newEmail, password: newPassword, add_to_my_team: addToMyTeam, make_admin: makeAdmin, make_unlimited: makeUnlimited },
                { headers: authHeaders() }
            );
            toast.success(`Created ${newEmail}. Share their password.`);
            setModalOpen(false);
            setNewName(""); setNewEmail(""); setNewPassword(""); setAddToMyTeam(true); setMakeAdmin(false); setMakeUnlimited(true);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create user");
        } finally { setCreating(false); }
    };

    if (!user?.is_admin) {
        return (
            <div className="border border-zinc-900 border-dashed p-12 text-center text-zinc-500" data-testid="admin-noaccess">
                Admin access required.
            </div>
        );
    }

    return (
        <div data-testid="admin-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ ADMIN</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Control room.</h1>
                    <p className="text-zinc-400 mt-2">Every account. Every team. Every plan.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => load()}
                        disabled={loading}
                        data-testid="admin-refresh-btn"
                        className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-4 py-3 rounded-md font-semibold transition-colors disabled:opacity-60"
                        title="Refresh to see new signups"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                    <button
                        onClick={() => setModalOpen(true)}
                        data-testid="admin-add-member-btn"
                        className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-5 py-3 rounded-md font-semibold transition-colors"
                    >
                        <Plus className="w-4 h-4" /> Add team member
                    </button>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                    <StatCard label="Total users" value={stats.total_users} icon={Users} testid="stat-users" />
                    <StatCard label="Teams" value={stats.total_teams} icon={Target} testid="stat-teams" />
                    <StatCard label="Conversations" value={stats.total_conversations} icon={Flame} testid="stat-convs" />
                    <StatCard label="Revenue" value={`$${stats.total_revenue_usd.toFixed(0)}`} icon={DollarSign} accent="text-green-400" testid="stat-revenue" />
                    <StatCard label="Paying teams" value={stats.paying_teams} icon={DollarSign} accent="text-green-400" testid="stat-paying" />
                    <StatCard label="Admin unlimited" value={stats.admin_unlimited_teams} icon={InfinityIcon} testid="stat-unlimited" />
                    <StatCard label="Free teams" value={stats.free_teams} icon={Users} testid="stat-free" />
                </div>
            )}

            {/* Platform settings — Stripe connect */}
            <div className="border border-zinc-900 bg-[#0c0c0c] p-6 mb-8" data-testid="stripe-settings">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Payouts</div>
                        <h2 className="font-heading font-bold text-xl flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-signal" strokeWidth={1.5} />
                            Stripe account
                        </h2>
                    </div>
                    {settings?.stripe_status === "connected" ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-green-500/30 bg-green-500/10 text-green-400 text-xs font-mono uppercase tracking-widest" data-testid="stripe-connected-badge">
                            <Check className="w-3.5 h-3.5" />
                            Connected · {settings.stripe_key_mode}
                        </div>
                    ) : settings?.stripe_status === "invalid" ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-red-500/30 bg-red-500/10 text-red-400 text-xs font-mono uppercase tracking-widest" data-testid="stripe-invalid-badge">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Key invalid
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-800 bg-zinc-900/40 text-zinc-400 text-xs font-mono uppercase tracking-widest" data-testid="stripe-default-badge">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Not connected
                        </div>
                    )}
                </div>

                {settings?.has_custom_stripe_key && settings?.stripe_status === "connected" ? (
                    <>
                        <div className="border border-green-500/20 bg-green-500/5 rounded-md p-4 mb-4" data-testid="stripe-account-card">
                            <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">Receiving payments to</div>
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                {settings.stripe_account?.email && (
                                    <div><span className="text-zinc-500">Account email: </span><span className="text-white">{settings.stripe_account.email}</span></div>
                                )}
                                {settings.stripe_account?.business_profile && (
                                    <div><span className="text-zinc-500">Business: </span><span className="text-white">{settings.stripe_account.business_profile}</span></div>
                                )}
                                {settings.stripe_account?.country && (
                                    <div><span className="text-zinc-500">Country: </span><span className="text-white">{settings.stripe_account.country}</span></div>
                                )}
                                <div><span className="text-zinc-500">Key: </span><span className="text-white font-mono">{settings.stripe_key_preview}</span></div>
                                <div>
                                    <span className="text-zinc-500">Charges enabled: </span>
                                    <span className={settings.stripe_account?.charges_enabled ? "text-green-400" : "text-amber-400"}>
                                        {settings.stripe_account?.charges_enabled ? "Yes" : "No — finish Stripe activation"}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-zinc-500">Payouts enabled: </span>
                                    <span className={settings.stripe_account?.payouts_enabled ? "text-green-400" : "text-amber-400"}>
                                        {settings.stripe_account?.payouts_enabled ? "Yes" : "No — add bank details in Stripe"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <a
                                href="https://dashboard.stripe.com/balance"
                                target="_blank" rel="noreferrer"
                                data-testid="stripe-dashboard-link"
                                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-white px-4 py-2 rounded-md text-sm transition-colors"
                            >
                                Open Stripe dashboard ↗
                            </a>
                            <button
                                onClick={() => saveStripeKey(true)}
                                disabled={savingStripe}
                                data-testid="disconnect-stripe-btn"
                                className="text-sm text-zinc-500 hover:text-red-400 disabled:opacity-50"
                            >
                                Disconnect
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {settings?.stripe_status === "invalid" && (
                            <div className="border border-red-500/30 bg-red-500/10 rounded-md p-4 mb-4 text-sm" data-testid="stripe-invalid-alert">
                                <div className="text-red-400 font-semibold mb-1 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" /> The saved key isn't working
                                </div>
                                <div className="text-zinc-300 mb-2">{settings.stripe_error || "Stripe rejected the saved key."}</div>
                                <div className="text-xs text-zinc-500">
                                    Common fixes: (1) re-copy your Secret key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-signal hover:underline">dashboard.stripe.com/apikeys</a>, (2) make sure you copied a <span className="text-white">Standard</span> key (not Restricted), (3) finish activating your account if using a live key.
                                </div>
                                <button
                                    onClick={() => saveStripeKey(true)}
                                    className="mt-3 text-xs text-zinc-400 hover:text-red-400 underline"
                                    data-testid="clear-bad-key-btn"
                                >
                                    Clear the bad key
                                </button>
                            </div>
                        )}

                        <p className="text-sm text-zinc-400 mb-4">
                            Paste your Stripe Secret Key (<code className="text-signal font-mono">sk_live_…</code> for production, <code className="text-signal font-mono">sk_test_…</code> for testing). Every customer checkout after this point deposits into your Stripe account. Get your key at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-signal hover:underline">dashboard.stripe.com/apikeys</a>.
                        </p>
                        <div className="flex gap-3 flex-wrap mb-3">
                            <input
                                type="password"
                                value={stripeKey}
                                onChange={(e) => { setStripeKey(e.target.value); setTestResult(null); }}
                                placeholder="sk_live_... or sk_test_..."
                                data-testid="stripe-key-input"
                                className="flex-1 min-w-[280px] bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                            />
                            <button
                                onClick={testStripeKey}
                                disabled={testingStripe || savingStripe || !stripeKey.trim()}
                                data-testid="test-stripe-btn"
                                className="border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white px-4 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
                            >
                                {testingStripe ? "Testing…" : "Test connection"}
                            </button>
                            <button
                                onClick={() => saveStripeKey(false)}
                                disabled={savingStripe || testingStripe || !stripeKey.trim()}
                                data-testid="save-stripe-btn"
                                className="bg-signal hover:bg-signal-hover text-white px-5 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60"
                            >
                                {savingStripe ? "Saving…" : "Connect Stripe"}
                            </button>
                        </div>

                        {testResult?.ok && testResult.account && (
                            <div className="border border-green-500/30 bg-green-500/10 rounded-md p-3 text-sm" data-testid="stripe-test-success">
                                <div className="text-green-400 font-semibold flex items-center gap-2 mb-1">
                                    <Check className="w-4 h-4" /> Valid key — ready to save
                                </div>
                                <div className="text-zinc-300 text-xs">
                                    Connected to <span className="text-white">{testResult.account.email || testResult.account.id}</span>
                                    {testResult.account.business_profile && <> · {testResult.account.business_profile}</>}
                                    {" · "}{testResult.account.mode} mode
                                    {!testResult.account.charges_enabled && <span className="text-amber-400"> · charges NOT enabled (finish Stripe activation)</span>}
                                </div>
                            </div>
                        )}
                        {testResult && !testResult.ok && (
                            <div className="border border-red-500/30 bg-red-500/10 rounded-md p-3 text-sm" data-testid="stripe-test-fail">
                                <div className="text-red-400 font-semibold flex items-center gap-2 mb-1">
                                    <AlertCircle className="w-4 h-4" /> Stripe rejected this key
                                </div>
                                <div className="text-zinc-300 text-xs">{testResult.error}</div>
                            </div>
                        )}

                        <p className="text-xs text-zinc-600 mt-3">
                            Your key is stored encrypted-at-rest. Only admins can see it (masked). We never save a key that Stripe rejects.
                        </p>
                    </>
                )}
            </div>

            {/* Filters */}
            <div className="border border-zinc-900 bg-[#0c0c0c] mb-4">
                <div className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between border-b border-zinc-900">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name or email…"
                        data-testid="admin-search"
                        className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                    />
                    <div className="flex gap-1">
                        {["all", "free", "pro"].map(p => (
                            <button
                                key={p}
                                onClick={() => setPlanFilter(p)}
                                data-testid={`admin-filter-${p}`}
                                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-md border transition-colors ${
                                    planFilter === p
                                        ? "border-signal bg-signal/10 text-white"
                                        : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-zinc-500">Loading users…</div>
                ) : users.length === 0 ? (
                    <div className="p-8 text-center text-zinc-500">No users match.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-900">
                                <tr>
                                    <th className="text-left p-3">User</th>
                                    <th className="text-left p-3">Team</th>
                                    <th className="text-left p-3">Plan</th>
                                    <th className="text-left p-3">Usage</th>
                                    <th className="text-left p-3">Joined</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-900">
                                {users.map((row) => (
                                    <tr key={row.id} className="hover:bg-zinc-900/30" data-testid={`admin-row-${row.id}`}>
                                        <td className="p-3 min-w-[220px]">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-white truncate">{row.name}</span>
                                                {row.is_admin && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-signal/10 text-signal border border-signal/30 uppercase tracking-widest" title="Admin">
                                                        <Shield className="w-2.5 h-2.5" /> Admin
                                                    </span>
                                                )}
                                                {row.role === "owner" && !row.is_admin && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-zinc-400 border border-zinc-800 uppercase tracking-widest" title="Team owner">
                                                        <Crown className="w-2.5 h-2.5" /> Owner
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-zinc-500 truncate">{row.email}</div>
                                        </td>
                                        <td className="p-3 text-xs text-zinc-400 truncate max-w-[180px]" title={row.team_name || ""}>
                                            {row.team_name || "—"}
                                            <div className="text-[10px] text-zinc-600">{row.active_team_members} member{row.active_team_members === 1 ? "" : "s"}</div>
                                        </td>
                                        <td className="p-3">
                                            {row.admin_unlimited ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono bg-signal/10 text-signal border border-signal/30 uppercase tracking-widest">
                                                    <InfinityIcon className="w-3 h-3" /> Admin · Pro
                                                </span>
                                            ) : row.plan === "pro" ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono bg-signal/10 text-signal border border-signal/30 uppercase tracking-widest capitalize">
                                                    {row.tier === "enterprise" ? "Enterprise" : row.tier === "standard" ? "Standard · $19" : "Pro · $49"}
                                                </span>
                                            ) : (
                                                <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-mono bg-zinc-900 text-zinc-400 border border-zinc-800 uppercase tracking-widest">Free</span>
                                            )}
                                            {row.paid_until && !row.admin_unlimited && (
                                                <div className="text-[10px] text-zinc-600 mt-0.5 font-mono">until {new Date(row.paid_until).toLocaleDateString()}</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs font-mono text-zinc-400">{row.generations_total} gen</td>
                                        <td className="p-3 text-xs text-zinc-500 font-mono">{new Date(row.created_at).toLocaleDateString()}</td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => toggleUnlimited(row)}
                                                    data-testid={`toggle-unlimited-${row.id}`}
                                                    className={`px-2.5 py-1 text-[10px] rounded-md border font-mono uppercase tracking-widest ${
                                                        row.admin_unlimited
                                                            ? "border-signal/40 bg-signal/10 text-signal hover:bg-signal/20"
                                                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                                                    }`}
                                                    title="Toggle free-unlimited for the whole team"
                                                >
                                                    {row.admin_unlimited ? "Unlimited" : "+Unlimited"}
                                                </button>
                                                <button
                                                    onClick={() => toggleAdmin(row)}
                                                    data-testid={`toggle-admin-${row.id}`}
                                                    className={`px-2.5 py-1 text-[10px] rounded-md border font-mono uppercase tracking-widest ${
                                                        row.is_admin
                                                            ? "border-signal/40 bg-signal/10 text-signal hover:bg-signal/20"
                                                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                                                    }`}
                                                    title="Toggle admin role"
                                                >
                                                    {row.is_admin ? "Admin" : "+Admin"}
                                                </button>
                                                {row.id !== user?.id && (
                                                    <button
                                                        onClick={() => deleteUser(row)}
                                                        data-testid={`delete-user-${row.id}`}
                                                        className="p-1.5 text-zinc-500 hover:text-red-400"
                                                        title="Delete user"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add member modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setModalOpen(false)} data-testid="add-member-modal">
                    <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="font-heading font-bold text-xl">Add team member</h3>
                            <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white" data-testid="close-modal">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={createMember} className="space-y-4" data-testid="add-member-form">
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Name</label>
                                <input required value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-name-input"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Email</label>
                                <input required type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Password</label>
                                <input required type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input"
                                    placeholder="Share this with them securely"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal font-mono" />
                            </div>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={addToMyTeam} onChange={(e) => setAddToMyTeam(e.target.checked)} data-testid="add-to-my-team"
                                    className="mt-1 accent-signal" />
                                <div>
                                    <div className="text-sm text-white">Add to my team</div>
                                    <div className="text-xs text-zinc-500">Inherits my team's plan (including unlimited if set)</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={makeUnlimited} onChange={(e) => setMakeUnlimited(e.target.checked)} data-testid="new-make-unlimited"
                                    className="mt-1 accent-signal" />
                                <div>
                                    <div className="text-sm text-white flex items-center gap-2">
                                        <InfinityIcon className="w-3.5 h-3.5 text-signal" />
                                        Grant free unlimited access
                                    </div>
                                    <div className="text-xs text-zinc-500">Skip billing — this user gets unlimited generations forever at $0.</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={makeAdmin} onChange={(e) => setMakeAdmin(e.target.checked)} data-testid="new-make-admin"
                                    className="mt-1 accent-signal" />
                                <div>
                                    <div className="text-sm text-white">Make admin</div>
                                    <div className="text-xs text-zinc-500">Full admin console access + free unlimited</div>
                                </div>
                            </label>
                            <button type="submit" disabled={creating} data-testid="create-member-btn"
                                className="w-full bg-signal hover:bg-signal-hover text-white px-6 py-3 rounded-md font-semibold transition-colors disabled:opacity-60">
                                {creating ? "Creating…" : "Create account"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
